import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useHttp from '../../hooks/http.hook';
import {
  flattenProductGroups,
  normalizeProductGroupsResponse,
} from '../../utils/productGroups';
import '../business/Business.css';
import '../replenish/ReplenishPage.css';
import './PreOrderProductsPage.css';
import CreateBusinessHeader from '../business/components/CreateBusinessHeader';
import CreateFooter from '../business/components/CreateFooter';
import PromotionActiveToggle from '../promotion/components/PromotionActiveToggle';

function PreOrderProductsPage() {
  const navigate = useNavigate();
  const { branchId } = useParams();
  const { loading, requestWithMeta } = useHttp();

  const [products, setProducts] = React.useState([]);
  const [productsLoading, setProductsLoading] = React.useState(true);
  const [submitError, setSubmitError] = React.useState(null);
  const [preOrderById, setPreOrderById] = React.useState({});
  const [initialPreOrderById, setInitialPreOrderById] = React.useState({});
  const [rentalBlocked, setRentalBlocked] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await requestWithMeta('/operator/business', 'GET');
      if (cancelled || !res?.ok) return;
      const business = res?.data?.business ?? res?.data ?? null;
      if (Number(business?.typeId) === 2) setRentalBlocked(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [requestWithMeta]);

  const backPath = `/branch/${branchId || 'store'}/replenish`;

  const fetchProducts = React.useCallback(async () => {
    setProductsLoading(true);
    setSubmitError(null);
    try {
      const res = await requestWithMeta('/operator/products', 'GET');
      if (!res?.ok) {
        setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось загрузить товары');
        return;
      }

      const normalized = flattenProductGroups(
        normalizeProductGroupsResponse(res?.data)
      );

      const nextPreOrder = {};
      normalized.forEach((p) => {
        const id = p?._id ?? p?.id;
        if (id == null) return;
        nextPreOrder[id] = !!p?.preOrder;
      });

      setProducts(normalized);
      setPreOrderById(nextPreOrder);
      setInitialPreOrderById(nextPreOrder);
    } finally {
      setProductsLoading(false);
    }
  }, [requestWithMeta]);

  React.useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const changedCount = React.useMemo(() => {
    return products.filter((p) => {
      const id = p?._id ?? p?.id;
      if (id == null) return false;
      return !!preOrderById[id] !== !!initialPreOrderById[id];
    }).length;
  }, [products, preOrderById, initialPreOrderById]);

  const enabledCount = React.useMemo(() => {
    return Object.values(preOrderById).filter(Boolean).length;
  }, [preOrderById]);

  const skeletonItems = React.useMemo(() => Array.from({ length: 4 }, (_, i) => i), []);
  const isSubmitDisabled = loading || productsLoading || changedCount === 0;

  const togglePreOrder = React.useCallback((productId) => {
    setPreOrderById((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  }, []);

  async function handleSubmit() {
    setSubmitError(null);

    const payload = products
      .map((p) => {
        const id = p?._id ?? p?.id;
        if (id == null) return null;
        if (!!preOrderById[id] === !!initialPreOrderById[id]) return null;
        return { productId: id, preOrder: !!preOrderById[id] };
      })
      .filter(Boolean);

    if (payload.length === 0) {
      setSubmitError('Нет изменений для сохранения');
      return;
    }

    const res = await requestWithMeta('/operator/products/pre-order', 'PUT', {
      products: payload,
    });

    if (!res?.ok) {
      setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось сохранить предзаказы');
      return;
    }

    navigate(backPath, { replace: true });
  }

  return (
    <div className="shop-create-root pre-order-page-root">
      <div className="shop-create-mobile">
        <CreateBusinessHeader
          title="Предзаказ"
          subtitle="Включите предзаказ для нужных товаров"
          onBack={() => navigate(backPath, { replace: true })}
        />

        <main className="shop-create-content">
          {rentalBlocked ? (
            <section className="shop-create-section">
              <div className="shop-create-label">Аренда</div>
              <div className="shop-create-help">Предзаказ недоступен для аренды. Свободные единицы считаются по выбранным датам.</div>
            </section>
          ) : null}
          {!rentalBlocked && submitError ? (
            <section className="shop-create-section shop-create-sectionError">
              <div className="shop-create-label">Ошибка</div>
              <div className="shop-create-help shop-create-helpTight">{submitError}</div>
            </section>
          ) : null}

          {!rentalBlocked ? (
          <section className="shop-create-section">
            <div className="replenish-page-listHeader">
              <div className="shop-create-label replenish-page-listTitle">Товары</div>
              <div className="shop-create-help shop-create-helpTight">
                {productsLoading
                  ? 'Загрузка…'
                  : `${enabledCount} из ${products.length} с предзаказом`}
              </div>
            </div>

            <div className="replenish-page-list">
              {productsLoading ? (
                skeletonItems.map((item) => (
                  <div key={item} className="replenish-page-item pre-order-page-item replenish-page-item--skeleton">
                    <div className="shop-create-skeleton replenish-page-skeletonImage" />
                    <div className="shop-create-skeleton shop-create-skeleton--text replenish-page-skeletonName" />
                    <div className="shop-create-skeleton pre-order-page-skeletonToggle" />
                  </div>
                ))
              ) : products.length === 0 ? (
                <div className="shop-create-help">Товары не найдены.</div>
              ) : (
                products.map((product) => {
                  const productId = product?._id ?? product?.id;
                  const enabled = !!preOrderById[productId];

                  return (
                    <div key={productId} className="replenish-page-item pre-order-page-item">
                      <img
                        className="replenish-page-image"
                        src={product?.imageUrl || ''}
                        alt={product?.name || 'product'}
                        loading="lazy"
                      />

                      <div className="replenish-page-main">
                        <div className="replenish-page-name">{product?.name || 'Без названия'}</div>
                        <div className={`pre-order-page-status ${enabled ? 'pre-order-page-status--on' : 'pre-order-page-status--off'}`}>
                          {enabled ? 'Предзаказ включён' : 'Предзаказ выключен'}
                        </div>
                      </div>

                      <PromotionActiveToggle
                        active={enabled}
                        disabled={loading}
                        onToggle={() => togglePreOrder(productId)}
                        label={`Предзаказ для ${product?.name || 'товара'}`}
                      />
                    </div>
                  );
                })
              )}
            </div>
          </section>
          ) : null}
        </main>

        <CreateFooter
          disabled={isSubmitDisabled || rentalBlocked}
          label={loading ? 'Сохранение…' : 'Сохранить'}
          onCreate={handleSubmit}
        />
      </div>
    </div>
  );
}

export default PreOrderProductsPage;
